import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../contexts/AuthContext';
import { updateAvailability } from '../../services/providerService';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { format, parse } from 'date-fns';

export default function AvailabilityPanel({ settings, setSettings }) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentSlotIndex, setCurrentSlotIndex] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const daysOfWeek = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' }
  ];

  const initialSlotState = {
    day_of_week: 0,
    start_time: new Date(new Date().setHours(9, 0, 0, 0)),
    end_time: new Date(new Date().setHours(17, 0, 0, 0))
  };

  const [formData, setFormData] = useState(initialSlotState);

  const handleOpenDialog = (slot = null, index = null) => {
    if (slot) {
      setIsEditing(true);
      setCurrentSlotIndex(index);
      
      // Convert string times to Date objects for the time picker
      const startTime = parseTimeString(slot.start_time);
      const endTime = parseTimeString(slot.end_time);
      
      setFormData({
        day_of_week: slot.day_of_week,
        start_time: startTime,
        end_time: endTime
      });
    } else {
      setIsEditing(false);
      setCurrentSlotIndex(null);
      setFormData(initialSlotState);
    }
    setOpenDialog(true);
  };

  // Helper to parse time string to Date object
  const parseTimeString = (timeString) => {
    if (typeof timeString === 'string') {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date;
    }
    return timeString;
  };

  // Helper to format Date to time string for display
  const formatTimeForDisplay = (date) => {
    if (!date) return '';
    return format(date, 'h:mm a');
  };

  // Helper to format Date to time string for API
  const formatTimeForAPI = (date) => {
    if (!date) return '';
    return format(date, 'HH:mm');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData(initialSlotState);
  };

  const handleDayChange = (event) => {
    setFormData({
      ...formData,
      day_of_week: event.target.value
    });
  };

  const handleStartTimeChange = (newTime) => {
    setFormData({
      ...formData,
      start_time: newTime
    });
  };

  const handleEndTimeChange = (newTime) => {
    setFormData({
      ...formData,
      end_time: newTime
    });
  };

  const handleSubmit = async () => {
    // Validate times
    if (formData.start_time >= formData.end_time) {
      setSnackbar({
        open: true,
        message: 'End time must be after start time',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Format time slots for API
      const newSlot = {
        day_of_week: formData.day_of_week,
        start_time: formatTimeForAPI(formData.start_time),
        end_time: formatTimeForAPI(formData.end_time)
      };
      
      let updatedAvailability;
      
      if (isEditing && currentSlotIndex !== null) {
        // Update existing slot
        updatedAvailability = [...settings.availability];
        updatedAvailability[currentSlotIndex] = newSlot;
      } else {
        // Add new slot
        updatedAvailability = [...settings.availability, newSlot];
      }
      
      // Sort by day of week and start time
      updatedAvailability.sort((a, b) => {
        if (a.day_of_week !== b.day_of_week) {
          return a.day_of_week - b.day_of_week;
        }
        return a.start_time.localeCompare(b.start_time);
      });
      
      // Update via API
      await updateAvailability(currentUser.uid, updatedAvailability);
      
      // Update local state
      setSettings({
        ...settings,
        availability: updatedAvailability
      });
      
      setSnackbar({
        open: true,
        message: isEditing ? 'Time slot updated successfully' : 'Time slot added successfully',
        severity: 'success'
      });
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving availability:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save availability. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (index) => {
    if (window.confirm('Are you sure you want to delete this time slot?')) {
      try {
        setLoading(true);
        
        // Remove the slot
        const updatedAvailability = [...settings.availability];
        updatedAvailability.splice(index, 1);
        
        // Update via API
        await updateAvailability(currentUser.uid, updatedAvailability);
        
        // Update local state
        setSettings({
          ...settings,
          availability: updatedAvailability
        });
        
        setSnackbar({
          open: true,
          message: 'Time slot deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting time slot:', error);
        setSnackbar({
          open: true,
          message: 'Failed to delete time slot. Please try again.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Group availability by day of week
  const getAvailabilityByDay = () => {
    const groupedAvailability = {};
    
    daysOfWeek.forEach(day => {
      groupedAvailability[day.value] = settings.availability.filter(
        slot => slot.day_of_week === day.value
      );
    });
    
    return groupedAvailability;
  };

  const groupedAvailability = getAvailabilityByDay();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Availability Settings
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Time Slot
          </Button>
        </Box>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          Set your regular working hours. Customers will be able to book appointments during these times.
        </Alert>
        
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Day</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {daysOfWeek.map((day) => (
                <TableRow key={day.value}>
                  <TableCell component="th" scope="row">
                    {day.label}
                  </TableCell>
                  <TableCell>
                    {groupedAvailability[day.value].length > 0 ? (
                      <Box>
                        {groupedAvailability[day.value].map((slot, index) => (
                          <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                            {parseTimeString(slot.start_time) && parseTimeString(slot.end_time) ? 
                              `${formatTimeForDisplay(parseTimeString(slot.start_time))} - ${formatTimeForDisplay(parseTimeString(slot.end_time))}` : 
                              'Invalid time format'}
                          </Typography>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not Available
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {groupedAvailability[day.value].map((slot, index) => {
                      // Find the actual index in the settings.availability array
                      const globalIndex = settings.availability.findIndex(
                        s => s.day_of_week === day.value && 
                             s.start_time === slot.start_time && 
                             s.end_time === slot.end_time
                      );
                      
                      return (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => handleOpenDialog(slot, globalIndex)}
                            aria-label="edit time slot"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDelete(globalIndex)}
                            aria-label="delete time slot"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Time Slot Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {isEditing ? 'Edit Time Slot' : 'Add New Time Slot'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="day-of-week-label">Day of Week</InputLabel>
                  <Select
                    labelId="day-of-week-label"
                    id="day-of-week"
                    value={formData.day_of_week}
                    label="Day of Week"
                    onChange={handleDayChange}
                  >
                    {daysOfWeek.map((day) => (
                      <MenuItem key={day.value} value={day.value}>
                        {day.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="Start Time"
                  value={formData.start_time}
                  onChange={handleStartTimeChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label="End Time"
                  value={formData.end_time}
                  onChange={handleEndTimeChange}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
}
